import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Styled Components
import {
  ButtonContainer,
  FormContainer,
  ModalContainerStyled,
} from './styles';

// Components
import InputText from '../../../inputs/InputText';
import { MainButton } from '../../../inputs/buttons/MainButton';
import { Typography } from '../../../display/Typography';

// Types
import { EditExternalCompanyFormType, ModalProps } from './types';

// Schema
import { EditExternalCompanyFormSchema } from './schema';

// GraphQL
import { client } from '../../../../gql/client';
import { UPDATE_EXTERNAL_COMPANY } from '../../../../gql/mutations/externalCompanies';

// Utils
import { showBackendErrorToast, showSuccessToast } from '../../../../utils/toasts';

/**
 * Renombrar una empresa externa del catálogo maestro.
 *
 * Mismo patrón que EditTeamModal: el modal lanza su propia mutation y avisa al
 * llamante con `onSuccess` para que revalide. La comprobación de duplicado se
 * hace aquí con el catálogo ya cargado (respuesta inmediata) y el backend la
 * repite como backstop.
 */
export const EditExternalCompanyModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'forms', 'common']);
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<EditExternalCompanyFormType>({
    defaultValues: { companyName: data?.company.companyName ?? '' },
    resolver: yupResolver(EditExternalCompanyFormSchema) as never,
  });

  /** Misma normalización que el backend: minúsculas y sin espacios. */
  const normalize = (name: string): string =>
    name.trim().toLowerCase().replace(/\s+/g, '');

  const onSubmit = async (values: EditExternalCompanyFormType) => {
    if (!data) return;

    const trimmed = values.companyName.trim();
    if (trimmed === data.company.companyName) {
      onClickClose();
      return;
    }

    const collides = data.existingCompanies.some(
      (company) =>
        company.id !== data.company.id &&
        normalize(company.companyName) === normalize(trimmed)
    );
    if (collides) {
      setDuplicateError(t('pages:management.errors.duplicateCompany'));
      return;
    }

    setIsSubmitting(true);
    try {
      await client.mutate({
        mutation: UPDATE_EXTERNAL_COMPANY,
        variables: {
          input: { externalCompanyId: data.company.id, companyName: trimmed },
        },
        refetchQueries: ['GetExternalCompanies'],
        awaitRefetchQueries: true,
      });
      showSuccessToast(t('pages:management.externalCompanyUpdated'));
      data.onSuccess?.();
      onClickClose();
    } catch (error) {
      showBackendErrorToast(
        error,
        t('pages:management.errors.companyGeneric')
      );
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContainerStyled
      width={42}
      title={t('pages:management.renameExternalCompany')}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      <FormContainer
        {...props}
        role="dialog"
        aria-modal="true"
        aria-label={t('pages:management.renameExternalCompany')}
      >
        <InputText
          {...register('companyName')}
          label={t('forms:company.name')}
          typeInput={'normal'}
          placeholder={t('forms:company.namePlaceholder')}
          borderError={!!errors.companyName?.message || !!duplicateError}
        />

        {duplicateError && (
          <Typography colorText="lightCoralRed" size="XS">
            {duplicateError}
          </Typography>
        )}

        <ButtonContainer>
          <MainButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <MainButton
            type="button"
            text={t('common:buttons.save')}
            variant="primary"
            onClick={() => {
              setDuplicateError(null);
              void handleSubmit(onSubmit)();
            }}
            disabled={isSubmitting}
          />
        </ButtonContainer>
      </FormContainer>
    </ModalContainerStyled>
  );
};
